// Coada de trimitere care supravietuieste inchiderii paginii.
//
// Invitatul scaneaza QR-ul, alege un clip si doua poze, apoi iese sa mai faca o
// poza si nu se mai intoarce. Fara asta, la urmatoarea deschidere a paginii nu
// mai stim ce avea de trimis: fisierele alese traiesc doar in memoria filei.
// Aici pastram fisierele in IndexedDB, iar la revenire uploadul continua singur
// din octetul confirmat de server.

const DB_NAME = 'qr-memories-uploads';
const DB_VERSION = 1;
const STORE = 'queue';

// Serverul tine sesiunile 24h; peste asta nu mai are ce relua.
const TTL_MS = 24 * 60 * 60 * 1000;

// Copiem fisierele in IndexedDB, deci ocupam spatiu real pe telefon. Peste
// aceste praguri renuntam la salvare: uploadul curent merge mai departe normal,
// doar ca nu se mai poate relua dupa inchiderea paginii.
const MAX_PERSIST_FILE = 100 * 1024 * 1024;
const MAX_PERSIST_TOTAL = 300 * 1024 * 1024;

function openDb() {
  return new Promise((resolve) => {
    // Safari in navigare privata, permisiuni blocate, cota plina: coada e un
    // plus, nu o conditie de functionare, deci esecul e tacut.
    let req;
    try { req = indexedDB.open(DB_NAME, DB_VERSION); } catch { return resolve(null); }
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE)) {
        const store = db.createObjectStore(STORE, { keyPath: 'id' });
        store.createIndex('slug', 'slug');
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => resolve(null);
    req.onblocked = () => resolve(null);
  });
}

// Intoarce { value } daca tranzactia s-a incheiat cu bine, null daca a esuat —
// nu doar valoarea, altfel o scriere reusita fara rezultat nu s-ar deosebi de
// un IndexedDB indisponibil.
function run(mode, fn) {
  return openDb().then((db) => {
    if (!db) return null;
    return new Promise((resolve) => {
      let out;
      let t;
      try { t = db.transaction(STORE, mode); } catch { db.close(); return resolve(null); }
      const store = t.objectStore(STORE);
      try { fn(store, (v) => { out = v; }); } catch { /* tranzactia se incheie oricum */ }
      t.oncomplete = () => { db.close(); resolve({ value: out }); };
      t.onerror = () => { db.close(); resolve(null); };
      t.onabort = () => { db.close(); resolve(null); };
    });
  });
}

// Salveaza fisierele care urmeaza sa plece. Intoarce cate au incaput.
export async function queueFiles(items) {
  let total = 0;
  const keep = items.filter((it) => {
    if (!it.file || it.file.size > MAX_PERSIST_FILE) return false;
    if (total + it.file.size > MAX_PERSIST_TOTAL) return false;
    total += it.file.size;
    return true;
  });
  if (keep.length === 0) return 0;

  const ok = await run('readwrite', (store) => {
    keep.forEach((it) => {
      store.put({
        id: it.id,
        slug: it.slug,
        file: it.file,
        name: it.file.name,
        type: it.file.type || '',
        lastModified: it.file.lastModified || Date.now(),
        uploaderName: it.uploaderName || '',
        message: it.message || '',
        at: Date.now()
      });
    });
  });
  return ok ? keep.length : 0;
}

export function dropQueued(id) {
  return run('readwrite', (store) => { store.delete(id); });
}

// Ce a ramas neterminat pentru evenimentul asta. Sterge din mers ce a expirat
// sau ce nu mai poate fi citit (sistemul a eliberat fisierul temporar).
export async function pendingFor(slug) {
  const res = await run('readonly', (store, done) => {
    const req = store.getAll();
    req.onsuccess = () => done(req.result || []);
  });
  const all = (res && res.value) || [];
  if (all.length === 0) return [];

  const expired = all.filter(r => Date.now() - (r.at || 0) > TTL_MS);
  if (expired.length) await run('readwrite', (store) => expired.forEach(r => store.delete(r.id)));

  const mine = all.filter(r => r.slug === slug && Date.now() - (r.at || 0) <= TTL_MS);
  const usable = [];
  for (const r of mine) {
    const file = r.file instanceof File
      ? r.file
      : new File([r.file], r.name, { type: r.type, lastModified: r.lastModified });
    // Pe iOS fisierul salvat poate ramane doar o referinta catre ceva ce
    // sistemul a sters intre timp; il verificam inainte sa promitem ca-l trimitem.
    try {
      await file.slice(0, 1).arrayBuffer();
      usable.push({ ...r, file });
    } catch {
      await dropQueued(r.id);
    }
  }
  return usable;
}

export function clearQueue(slug) {
  return run('readwrite', (store, done) => {
    const req = store.getAll();
    req.onsuccess = () => {
      (req.result || []).filter(r => !slug || r.slug === slug).forEach(r => store.delete(r.id));
      done(true);
    };
  });
}

export const QUEUE_LIMITS = { MAX_PERSIST_FILE, MAX_PERSIST_TOTAL };
